import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Modal,
  Stack,
  Alert,
  Checkbox,
  Textarea,
  SegmentedControl,
  Text,
} from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Patient, Encounter, CodeableConcept } from '@medplum/fhirtypes';
import { CodeableConceptInput } from '@medplum/react';
import { notifications } from '@mantine/notifications';
import { DateInputWrapper } from '@/components/inputs/DateInputWrapper';
import FormActionButtons, {
  FormActionButtonsRef,
} from '@/components/FormActionButtons';
import { isEqual } from 'lodash';
import { ARKON, HL7 } from '@consts/systems.consts';
import {
  getConditionCode,
  getClinicalStatus,
  getOnsetDate,
  getNotes,
  getProblemListItem,
  setConditionCode,
  setClinicalStatus,
  setOnsetDate,
  setNotes,
  setProblemListItem,
  hasConditionCode,
  hasClinicalStatus,
  createNewConditionInputItem,
  ConditionInputItem,
} from '@/utils/condition.utils';

export interface ConditionInputModalProps {
  opened: boolean;
  onClose: () => void;
  /** Function to save condition to server. Returns the saved condition with server-assigned IDs. */
  onSave: (
    condition: ConditionInputItem,
    isMedicalHistory?: boolean
  ) => Promise<ConditionInputItem>;
  /** Callback called after successful save. Used to update parent component state. */
  onSaveSuccess?: (condition: ConditionInputItem) => void;
  condition?: ConditionInputItem;
  mode: 'add' | 'edit';
  patient: Patient;
  encounter: Encounter | undefined;
  isMedicalHistory?: boolean;
}

// Default empty condition for new entries
const createEmptyCondition = (): ConditionInputItem =>
  createNewConditionInputItem();

export const ConditionInputModal = ({
  opened,
  onClose,
  onSave,
  onSaveSuccess,
  condition,
  mode,
  encounter,
  isMedicalHistory = false,
}: ConditionInputModalProps) => {
  const { t } = useTranslation(['patient-pages']);

  // Form state - using ConditionInputItem directly
  const [formData, setFormData] = useState<ConditionInputItem>(
    createEmptyCondition()
  );
  const [validationError, setValidationError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Key to force CodeableConceptInput remount on each modal open
  const [inputKey, setInputKey] = useState(0);

  // Refs for FormActionButtons pattern
  const formActionsRef = useRef<FormActionButtonsRef>(null);
  const baselineRef = useRef<ConditionInputItem | null>(null);
  const baselineDatesRef = useRef<{ onset: Date | null } | null>(null);
  const [baselineSet, setBaselineSet] = useState(false);

  // Date state (separate because FHIR uses strings, form uses Date)
  const [onsetDate, setOnsetDateState] = useState<Date | null>(null);

  // Reset form when modal opens or condition changes
  useEffect(() => {
    if (opened) {
      // Increment key to force CodeableConceptInput to remount with fresh defaultValue
      setInputKey((prev) => prev + 1);
      setValidationError(null);
      setBaselineSet(true); // Flag to capture baseline after state is set
      if (condition) {
        setFormData({ ...condition });
        setOnsetDateState(getOnsetDate(condition));
      } else {
        const empty = createEmptyCondition();
        setFormData(empty);
        setOnsetDateState(null);
      }
    } else {
      setBaselineSet(false);
    }
  }, [opened, condition]);

  // Capture baseline after form state is initialized
  useEffect(() => {
    if (opened && baselineSet) {
      baselineRef.current = { ...formData };
      baselineDatesRef.current = { onset: onsetDate };
      setBaselineSet(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, onsetDate]);

  // Update formData when date changes
  const updateDate = useCallback((date: Date | null) => {
    setFormData((prev) => setOnsetDate(prev, date));
  }, []);

  useEffect(() => {
    updateDate(onsetDate);
  }, [onsetDate, updateDate]);

  // Clear validation error on any change
  const clearValidation = () => {
    if (validationError) {
      setValidationError(null);
    }
  };

  // Change detection for FormActionButtons
  const hasUnsavedChanges = (): boolean => {
    if (!baselineRef.current) {
      return false;
    }

    try {
      const formChanged = !isEqual(formData, baselineRef.current);
      const datesChanged = !isEqual(
        { onset: onsetDate },
        baselineDatesRef.current
      );
      return formChanged || datesChanged;
    } catch {
      return true;
    }
  };

  // Discard handler for FormActionButtons
  const handleDiscardChanges = () => {
    if (baselineRef.current) {
      setFormData({ ...baselineRef.current });
    }
    if (baselineDatesRef.current) {
      setOnsetDateState(baselineDatesRef.current.onset);
    }
    setInputKey((prev) => prev + 1); // Reset CodeableConceptInput
    setValidationError(null);
  };

  const handleSave = async () => {
    // Validate encounter
    if (!encounter) {
      setValidationError(
        t('examination.conditions.encounterRequired', {
          defaultValue: 'Encounter is required to save a condition',
        })
      );
      return;
    }

    // Validate required fields
    if (!hasConditionCode(formData)) {
      setValidationError(
        t('examination.conditions.diagnosisRequired', {
          defaultValue: 'Please select or enter a diagnosis',
        })
      );
      return;
    }
    if (!hasClinicalStatus(formData)) {
      setValidationError(
        t('examination.conditions.clinicalStatusRequired', {
          defaultValue: 'Please select a clinical status',
        })
      );
      return;
    }

    // Save to server
    setSaving(true);
    try {
      const savedCondition = await onSave(formData, isMedicalHistory);
      // Notify parent component of successful save
      if (onSaveSuccess) {
        onSaveSuccess(savedCondition);
      }
      onClose();
    } catch (error) {
      notifications.show({
        color: 'red',
        title: t('examination.conditions.saveErrorTitle', {
          defaultValue: 'Failed to save condition',
        }),
        message: String(error),
      });
    } finally {
      setSaving(false);
    }
  };

  // Extract current values using getters
  const currentCode = getConditionCode(formData);
  const currentClinicalStatus = getClinicalStatus(formData);
  const currentNotes = getNotes(formData);
  const currentProblemListItem = getProblemListItem(formData);

  // Get current clinical status code for SegmentedControl
  // Default to 'active' if no status is set
  const currentClinicalStatusCode =
    currentClinicalStatus?.coding?.[0]?.code || 'active';

  // Clinical status options for SegmentedControl
  const clinicalStatusOptions = [
    {
      value: HL7.TERMINOLOGY.CS.CONDITION.CLINICAL.CODES.ACTIVE,
      label: t('examination.conditions.clinicalStatusOptions.active', {
        ns: 'patient-pages',
        defaultValue: 'Active',
      }),
    },
    {
      value: HL7.TERMINOLOGY.CS.CONDITION.CLINICAL.CODES.INACTIVE,
      label: t('examination.conditions.clinicalStatusOptions.inactive', {
        ns: 'patient-pages',
        defaultValue: 'Inactive',
      }),
    },
    {
      value: HL7.TERMINOLOGY.CS.CONDITION.CLINICAL.CODES.RESOLVED,
      label: t('examination.conditions.clinicalStatusOptions.resolved', {
        ns: 'patient-pages',
        defaultValue: 'Resolved',
      }),
    },
  ];

  // Handle clinical status change from SegmentedControl
  const handleClinicalStatusChange = (value: string) => {
    const clinicalStatusCodeableConcept: CodeableConcept = {
      coding: [
        {
          system: HL7.TERMINOLOGY.CS.CONDITION.CLINICAL.BASE,
          code: value,
          display: t(`examination.conditions.clinicalStatusOptions.${value}`, {
            ns: 'patient-pages',
            defaultValue: value,
          }),
        },
      ],
    };
    setFormData((prev) =>
      setClinicalStatus(prev, clinicalStatusCodeableConcept)
    );
    clearValidation();
  };

  return (
    <Modal
      opened={opened}
      onClose={() => formActionsRef.current?.handleGuardedClose()}
      closeOnClickOutside={false}
      closeOnEscape={false}
      title={
        mode === 'add'
          ? t('examination.conditions.addCondition', {
              defaultValue: 'Add Condition',
            })
          : t('examination.conditions.editCondition', {
              defaultValue: 'Edit Condition',
            })
      }
      size="lg"
    >
      <Stack gap="md">
        {/* Validation Error */}
        {validationError && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
          >
            {validationError}
          </Alert>
        )}

        {/* Diagnosis/Code */}
        <CodeableConceptInput
          key={`code-${inputKey}`}
          path="Condition.code"
          binding={ARKON.VS.ICD_10_EL}
          label={t('examination.conditions.diagnosis', {
            ns: 'patient-pages',
            defaultValue: 'Diagnosis',
          })}
          name="condition-code"
          maxValues={1}
          defaultValue={currentCode}
          onChange={(code) => {
            setFormData((prev) => setConditionCode(prev, code));
            clearValidation();
          }}
          outcome={undefined}
          placeholder={t('examination.conditions.diagnosisPlaceholder', {
            ns: 'patient-pages',
            defaultValue: 'e.g. Heart failure',
          })}
          creatable={true}
          required
        />

        {/* Clinical Status */}
        <Stack gap={2}>
          <Text size="sm" fw={500}>
            {t('examination.conditions.clinicalStatus', {
              ns: 'patient-pages',
              defaultValue: 'Clinical Status',
            })}
          </Text>
          <SegmentedControl
            fullWidth
            data={clinicalStatusOptions}
            value={currentClinicalStatusCode}
            onChange={handleClinicalStatusChange}
          />
        </Stack>

        {/* Onset Date */}
        <DateInputWrapper
          name="onsetDate"
          label={t('examination.conditions.onsetDate', {
            ns: 'patient-pages',
            defaultValue: 'Onset Date',
          })}
          value={onsetDate}
          onChange={(date) => {
            setOnsetDateState(date);
            clearValidation();
          }}
        />

        {/* Add to Problem List */}
        <Checkbox
          checked={currentProblemListItem}
          label={t('examination.conditions.addToProblemList', {
            ns: 'patient-pages',
            defaultValue: 'Add to problem list',
          })}
          onChange={(event) => {
            setFormData((prev) =>
              setProblemListItem(prev, event.currentTarget.checked)
            );
            clearValidation();
          }}
        />

        {/* Notes */}
        <Textarea
          label={t('examination.conditions.notes', {
            ns: 'patient-pages',
            defaultValue: 'Notes',
          })}
          placeholder={t('examination.conditions.notesPlaceholder', {
            ns: 'patient-pages',
            defaultValue: 'Additional notes about this condition...',
          })}
          value={currentNotes}
          onChange={(e) => {
            setFormData((prev) => setNotes(prev, e.currentTarget.value));
            clearValidation();
          }}
          minRows={2}
          maxRows={4}
        />

        {/* Actions */}
        <FormActionButtons
          ref={formActionsRef}
          onSave={handleSave}
          onDiscard={handleDiscardChanges}
          onClose={onClose}
          hasUnsavedChanges={hasUnsavedChanges()}
          isLoading={saving}
          saveLabel={
            mode === 'add'
              ? t('examination.conditions.addButton', {
                  defaultValue: 'Add',
                })
              : t('examination.conditions.saveButton', {
                  defaultValue: 'Save',
                })
          }
        />
      </Stack>
    </Modal>
  );
};
